import React from 'react';
import { apparelSizeFromSku, type ApparelSize } from '@/shared/utils/skuSize';
import type { OrderItem } from '../../../types/apiTypes';
import { renderSkuWithSoftBreaks } from '../../../utils/skuSoftBreak';
import ProductImage from '../../shared/ProductImage';

const SIZE_BADGE_CLASS: Readonly<Record<ApparelSize, string>> = {
  XS: 'item-size-badge-xs',
  S: 'item-size-badge-s',
  M: 'item-size-badge-m',
  L: 'item-size-badge-l',
  XL: 'item-size-badge-xl',
};

interface PickLineChipProps {
  readonly item: OrderItem;
  readonly hasWarehouseNote: boolean;
  readonly onSelectProduct: (item: OrderItem) => void;
  readonly onSelectShelf: (item: OrderItem) => void;
  readonly shelfScanEnabled: boolean;
  readonly onModify?: (item: OrderItem) => void;
  readonly componentsCollapsed?: boolean;
  readonly onToggleComponents?: () => void;
}

function shelfDisplay(item: OrderItem): React.ReactNode {
  if (typeof item.shelfDisplay === 'string') {
    return item.shelfDisplay;
  }
  if (item.shelfDisplay?.stockLocation) {
    return (
      <>
        <span className="item-shelf-stock-label">Stock:</span>
        <span>{item.shelfDisplay.stockLocation}</span>
      </>
    );
  }
  return 'N/A';
}

function collapseChevronPath(collapsed: boolean): string {
  return collapsed ? 'M6 9 L12 15 L18 9' : 'M6 15 L12 9 L18 15';
}

function PickLineChip({
  item,
  hasWarehouseNote,
  onSelectProduct,
  onSelectShelf,
  shelfScanEnabled,
  onModify,
  componentsCollapsed = false,
  onToggleComponents,
}: PickLineChipProps): React.ReactElement {
  const hasUnitsInName = Boolean(item.name && /\(\d+\s*units?\)\s*$/i.test(item.name));
  const size = apparelSizeFromSku(item.sku);

  const activateProduct = (): void => {
    onSelectProduct(item);
  };

  return (
    <div className="item-row-wrapper">
      <div
        className={`item-line-content${hasWarehouseNote ? ' has-warehouse-note' : ''}`}
        role="button"
        tabIndex={0}
        onClick={activateProduct}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activateProduct();
          }
        }}
      >
        {item.sku ? (
          <div className="item-line-thumb">
            <ProductImage sku={item.sku} alt={item.name || item.sku} />
            {size ? (
              <span className={`item-size-badge ${SIZE_BADGE_CLASS[size]}`} aria-label={`Size ${size}`}>
                {size}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="item-line-text">
          <div className="item-sku">{renderSkuWithSoftBreaks(item.sku)}</div>
          <div className="item-name">
            {hasUnitsInName ? (
              <span className="item-name-with-units">
                {item.name.slice(0, item.name.lastIndexOf('(')).trim()}
                {onModify ? (
                  <span className="item-units-row">
                    <span className="item-units">{`- ${item.name.slice(item.name.lastIndexOf('(') + 1, -1)}`}</span>
                    <button
                      type="button"
                      className="item-transfer-modify item-transfer-modify-inline"
                      onClick={(event) => {
                        event.stopPropagation();
                        onModify(item);
                      }}
                    >
                      Actions
                    </button>
                  </span>
                ) : (
                  <span className="item-units">{`- ${item.name.slice(item.name.lastIndexOf('(') + 1, -1)}`}</span>
                )}
              </span>
            ) : (
              item.name
            )}
          </div>
        </div>
        {onToggleComponents ? (
          <button
            type="button"
            className="bundle-collapse-toggle"
            aria-expanded={!componentsCollapsed}
            aria-label={componentsCollapsed ? 'Show bundle components' : 'Hide bundle components'}
            onClick={(event) => {
              event.stopPropagation();
              onToggleComponents();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          >
            <svg
              className="bundle-collapse-toggle-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d={collapseChevronPath(componentsCollapsed)}
                fill="none"
                stroke="currentColor"
                strokeWidth="3.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}
      </div>
      <button
        type="button"
        className={`item-shelf-index${shelfScanEnabled ? ' item-shelf-index-scanable' : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          onSelectShelf(item);
        }}
        aria-label={
          shelfScanEnabled
            ? `Scan ${item.sku} at this location`
            : `Shelf location for ${item.sku}`
        }
      >
        {shelfDisplay(item)}
      </button>
      {onModify ? (
        <button
          type="button"
          className="item-transfer-modify item-transfer-modify-shelf"
          onClick={(event) => {
            event.stopPropagation();
            onModify(item);
          }}
        >
          Actions
        </button>
      ) : null}
    </div>
  );
}

export default PickLineChip;
